import React from 'react';
import { renderMarkdown } from '../../utils/markdown';
import {
  MessageActionMenu,
  MessageReactionStrip,
  PinnedMessagesStrip,
  ReplyComposerPreview,
  ReplyContext,
  messagePreview
} from './MessageActions';

const CHAT_EMOJIS = [
  '\u{1F600}', '\u{1F604}', '\u{1F602}', '\u{1F60A}', '\u{1F60D}', '\u{1F60E}',
  '\u{1F91D}', '\u{1F64F}', '\u{1F44D}', '\u{1F525}', '\u{2728}', '\u{1F680}',
  '\u{1F512}', '\u{1F6E1}\u{FE0F}', '\u{26A1}', '\u{2705}', '\u{2757}', '\u{2764}\u{FE0F}'
];

export const ChatPane = ({
  activeChatContact,
  chatMessages,
  activeIdentity,
  chatInputText,
  setChatInputText,
  handleSendChatMessage,
  setActiveChatContact,
  showEmojiPicker,
  setShowEmojiPicker,
  handleDeleteChatMessage,
  handleClearDirectChat,
  openContactProfile,
  messageMeta = {},
  onToggleMessagePin,
  onToggleMessageSaved,
  onReactToMessage,
  messageReplyTarget,
  setMessageReplyTarget,
  t,
  displayGaiaID,
  getSenderRoles
}) => {
  const scrollRef = React.useRef(null);
  const longPressTimerRef = React.useRef(null);
  const [actionMessageId, setActionMessageId] = React.useState(null);

  const appendChatEmoji = emoji => {
    setChatInputText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const visibleMessages = React.useMemo(() => {
    if (!activeChatContact || !activeIdentity) return [];
    return chatMessages.filter(msg =>
      (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
      (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
    );
  }, [activeChatContact, activeIdentity, chatMessages]);

  const lastMessageId = visibleMessages.length > 0 ? visibleMessages[visibleMessages.length - 1].id : '';

  React.useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [activeChatContact?.ID, lastMessageId, visibleMessages.length]);

  React.useEffect(() => () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
  }, []);

  const openMessageActions = (event, messageId) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMessageId(messageId);
  };

  const startLongPress = messageId => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      setActionMessageId(messageId);
    }, 520);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const jumpToMessage = messageId => {
    if (!scrollRef.current) return;
    const target = scrollRef.current.querySelector(`[data-message-id="${messageId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const replyToMessage = message => {
    setMessageReplyTarget({
      messageId: message.id,
      sender: message.sender,
      senderGaia: message.senderGaia,
      bodyPreview: messagePreview(message),
      createdAt: message.createdAt
    });
  };

  if (!activeChatContact) {
    return (
      <div className="chat-container empty-chat-state">
        <h3>{t('kein_chat_ausgewaehlt') || 'Kein Chat ausgewahlt'}</h3>
        <p>{t('select_contact_chat') || 'Wahle einen Kontakt aus dem linken Panel aus, um einen quantensicheren E2E-Chat zu starten.'}</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <header className="reader-header chat-reader-header">
        <button type="button" className="mobile-back-btn" onClick={() => setActiveChatContact(null)}>
          {'<'} {t('quanten_chat') || 'Chats'}
        </button>
        <div className="chat-peer-header">
          <div className="chat-peer-icon" aria-hidden="true">{'\u{1F4AC}'}</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="link-button contact-name-button" onClick={() => openContactProfile(activeChatContact.gaiaID)}>
                {activeChatContact.displayName}
              </button>
              {getSenderRoles && getSenderRoles(activeChatContact.gaiaID)?.map(role => {
                let bg = 'rgba(255,255,255,0.05)';
                let color = 'var(--text-secondary)';
                let border = '1px solid var(--border-color)';
                let label = role;

                if (role === 'node_operator') {
                  bg = 'rgba(168, 85, 247, 0.15)';
                  color = '#c084fc';
                  border = '1px solid rgba(168, 85, 247, 0.3)';
                  label = t('node_operator') || 'Node Operator';
                } else if (role === 'senior_reviewer') {
                  bg = 'rgba(249, 115, 22, 0.15)';
                  color = '#fdba74';
                  border = '1px solid rgba(249, 115, 22, 0.3)';
                  label = t('senior_reviewer') || 'Senior Reviewer';
                } else if (role === 'trusted_reviewer') {
                  bg = 'rgba(20, 184, 166, 0.15)';
                  color = '#2dd4bf';
                  border = '1px solid rgba(20, 184, 166, 0.3)';
                  label = t('trusted_reviewer') || 'Trusted Reviewer';
                } else {
                  label = t(role) || role;
                }

                return (
                  <span
                    key={role}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1px 6px',
                      borderRadius: '10px',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      background: bg,
                      color: color,
                      border: border,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            <span>{displayGaiaID(activeChatContact.gaiaID)}</span>
          </div>
        </div>
        <button type="button" className="btn-secondary chat-header-action" onClick={handleClearDirectChat}>
          {t('clear_chat') || 'Clear Chat'}
        </button>
      </header>

      <PinnedMessagesStrip
        messages={visibleMessages}
        messageMeta={messageMeta}
        onJumpToMessage={jumpToMessage}
        t={t}
      />

      <div className="chat-messages gaia-scrollbar" ref={scrollRef} onClick={() => setActionMessageId(null)}>
        {visibleMessages.map(msg => {
          const isOutgoing = msg.sender === activeIdentity.GaiaID;
          const meta = messageMeta[msg.id] || {};
          return (
            <div
              key={msg.id}
              data-message-id={msg.id}
              className={`chat-bubble ${isOutgoing ? 'outgoing' : 'incoming'} ${meta.pinned ? 'pinned' : ''} ${meta.saved ? 'saved' : ''}`}
              onContextMenu={event => openMessageActions(event, msg.id)}
              onTouchStart={() => startLongPress(msg.id)}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
            >
              <ReplyContext replyTo={msg.replyTo} t={t} />
              <div>{renderMarkdown(msg.body)}</div>
              <MessageReactionStrip meta={meta} />
              <div className="chat-bubble-meta">
                <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                {meta.saved && <span>{t('message_saved') || 'Saved'}</span>}
                <button type="button" className="chat-delete-btn" onClick={() => handleDeleteChatMessage(msg.id)}>
                  {t('delete') || 'Delete'}
                </button>
                {msg.untrusted && <span className="chat-untrusted">{t('untrusted') || 'Untrusted'}</span>}
              </div>
              <MessageActionMenu
                open={actionMessageId === msg.id}
                message={msg}
                meta={meta}
                onClose={() => setActionMessageId(null)}
                onTogglePin={onToggleMessagePin}
                onToggleSave={onToggleMessageSaved}
                onReply={replyToMessage}
                onReact={onReactToMessage}
                t={t}
              />
            </div>
          );
        })}
        {visibleMessages.length === 0 && (
          <div className="chat-empty-hint">
            {t('chat_start_hint') || 'Starten Sie die Konversation! Alle Chat-Nachrichten sind quantensicher E2E verschlusselt.'}
          </div>
        )}
      </div>

      <div className="chat-composer-stack">
        <ReplyComposerPreview
          replyTarget={messageReplyTarget}
          onClear={() => setMessageReplyTarget(null)}
          t={t}
        />
        <form className="chat-input-row" onSubmit={handleSendChatMessage}>
          <div className="emoji-control">
            <button type="button" className="btn-secondary emoji-toggle" onClick={() => setShowEmojiPicker(prev => !prev)}>
              {'\u{1F642}'}
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker" role="listbox" aria-label="Emoji Auswahl">
                {CHAT_EMOJIS.map(emoji => (
                  <button type="button" key={emoji} onClick={() => appendChatEmoji(emoji)}>
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            className="input-field"
            placeholder={t('chat_input_placeholder') || 'Sichere Nachricht eingeben...'}
            value={chatInputText}
            onChange={event => setChatInputText(event.target.value)}
            required
          />
          <button type="submit" className="btn-primary">
            {t('senden') || 'Senden'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPane;
